import {
  PaymentControllerService,
  PaymentInfoSetVo,
  SupportModuleControllerService,
} from '@/__generated__/CommonApi';
import { RegularPayInfoVo } from '@/__generated__/FrontApi';
import Button from '@/components/Button';
import PopupModal from '@/components/PopupModal';
import Section from '@/components/Section';
import { NotiBox } from '@/components/Text/TextNoti';
import InicisMobileOnceForm, {
  InicisMobileOnceRef,
} from '@/components/ThirdParty/InicisMobileOnceForm';
import InicisPcOnceForm, {
  InicisPcOnceRef,
} from '@/components/ThirdParty/InicisPcOnceForm';
import KcpMobileForm, {
  KcpMobileRef,
} from '@/components/ThirdParty/KcpMobileForm';
import KcpPcForm, { KcpPcRef } from '@/components/ThirdParty/KcpPcForm';
import MobiliansOnceForm, {
  MobiliansOnceRef,
} from '@/components/ThirdParty/MobiliansOnceForm';
import NaverPayForm, {
  NaverPayRef,
} from '@/components/ThirdParty/NaverPayForm';
import {
  getDonateEncKey,
  resetDonateEncKey,
} from '@/components/ThirdParty/SirenNameCheck';
import { Tit } from '@/components/Titles';
import { breakpoint, Breakpoints } from '@/helpers/BreakpointHelper';
import { isMobileDevice } from '@/helpers/BrowserHelper';
import GtmHelper from '@/helpers/GtmHelper';
import PaymentHelper, {
  INICIS_PAY_METHOD_MOBILE_MAP,
  INICIS_PAY_METHOD_PC_MAP,
  KCP_PAY_METHOD_MOBILE_MAP,
  KCP_PAY_METHOD_PC_MAP,
  KCP_BANK_MAP,
  PAYTYPE_ONCE_BANKING,
  PAYTYPE_ONCE_CARD,
  PAYTYPE_ONCE_NAVERPAY,
  PAYTYPE_ONCE_PHONE,
  PAYTYPE_REGULAR_AUTO_BANKING,
  PAYTYPE_REGULAR_CARD,
  PAYTYPE_REGULAR_NAVERPAY,
  getPgType,
  getKcpVaDate,
} from '@/helpers/PaymentHelper';
import {
  getSupportProductName,
  useSupportCategoriesForPayment,
  ENUM_SUPPORTER_TYPE,
  MEMBER_TYPE_FLATMAP,
  MEMBER_TYPE_INDIVIDUAL,
  MEMBER_TYPE_SCHOOL,
  SUPPORT_TERMS,
  SUPPORT_TERM_ONCE,
  SUPPORT_TERM_REGULAR,
} from '@/helpers/SupportHelper';
import LayoutWithTitle from '@/layouts/LayoutWithTitle';
import DonateBanner, {
  BANNER_DISP_LOC_CD__DONATE_S1,
  BANNER_DISP_LOC_CD__DONATE_S2,
  BANNER_DISP_LOC_CD__DONATE_S3,
  BannerInfoData,
} from '@/page-blocks/donate/DonateBanner';
import DonateProcessOverlay, {
  DonateProcessStage,
} from '@/page-blocks/donate/DonateProcessOverlay';
import DonateStep1, {
  SupportInitialValues,
  SupportStep1Data,
} from '@/page-blocks/donate/DonateStep1';
import DonateStep2, {
  SupportStep2Data,
} from '@/page-blocks/donate/DonateStep2';
import DonateStep3, {
  SupportStep3Data,
} from '@/page-blocks/donate/DonateStep3';
import LocalStorage from '@/services/LocalStorage';
import SessionStorage, {
  SESSION_STORAGE_KEYS__DONATE_TRACKING_CODE,
} from '@/services/SessionStorage';
import { usePopupStore } from '@/stores/PopupStore';
import { useUserStore } from '@/stores/UserStore';
import { useLocation } from '@reach/router';
import axios from 'axios';
import dayjs from 'dayjs';
import { PageProps } from 'gatsby';
import { throttle } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import BeforeUnloadComponent from 'react-beforeunload-component';
import styled, { css } from 'styled-components';

const SectionDonate = styled(Section)`
  padding: 0;
  padding-bottom: 63px;
`;

const Container = styled.div<{
  sticky: boolean;
}>`
  max-width: 544px;
  margin: 0 auto;
  ${({ sticky }) => sticky && 'padding-top: 66px;'}

  ${breakpoint(`tablet`)} {
    width: 100%;
    padding: 0 20px;
    ${({ sticky }) => sticky && 'padding-top: 62px;'}
  }

  ${breakpoint(`mobile`)} {
    width: 100%;
    padding: 0 20px;
    ${({ sticky }) => sticky && 'padding-top: 62px;'}
  }
`;
const StepHead = styled.div<{ showBlueBorder?: boolean }>`
  line-height: 1.2;
  padding: 15px 0 17px;
  color: #b7b8ba;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  position: relative;

  border-bottom: ${({ showBlueBorder }) =>
    showBlueBorder ? '1px solid #1cabe2' : 'none'};
  margin-bottom: ${({ showBlueBorder }) => (showBlueBorder ? '30px' : '0')};

  &.agree-head {
    margin-top: 46px;
    border-top: 1px solid #d4d4d4;
    border-bottom: 1px solid #b7b8ba;
    padding-bottom: 16px;
  }
`;
const StepBody = styled.div`
  display: none;
`;
const SupportStepSticky = css`
  position: fixed;
  top: 120px;
  background: #fff;
  z-index: 10;

  ${breakpoint(`tablet`)} {
    top: 56px;
    width: calc(100% - 40px);
  }

  ${breakpoint(`mobile`)} {
    top: 56px;
    width: calc(100% - 40px);
  }
`;
const SupportStep = styled.ol<{
  sticky: boolean;
}>`
  width: 100%;
  max-width: 544px;
  display: flex;
  padding-top: 24px;
  ${({ sticky }) => sticky && SupportStepSticky}
`;
const StepList = styled.li<{ active?: boolean }>`
  flex: 1;
  text-align: center;
  color: #777779;
  padding-bottom: 14px;
  border-bottom: 2px solid #dbdbdb;
  ${({ active }) => {
    if (active) {
      return `
        color: #1CABE2;
        font-weight: 700;
        border-bottom: 4px solid #1CABE2;
      `;
    }
    return ``;
  }}
`;
const ToggleBoxActive = css`
  h2 {
    color: #2d2926;
  }
  form {
    display: block;
  }
  border-bottom: none;
  ${StepBody} {
    display: block;
  }
`;
const ToggleBox = styled.div<{ active: boolean; hidden: boolean }>`
  h2 {
    color: #b7b8ba;
  }
  form {
    display: none;
  }
  border-bottom: 1px solid #b7b8ba;
  ${StepBody} {
    display: none;
  }
  ${NotiBox} {
    margin-bottom: 24px;
  }
  ${({ active }) => active && ToggleBoxActive};
  ${({ hidden }) => hidden && `display: none;`};
`;
const FormResult = styled.div`
  color: #1cabe2;
  margin-left: 20px;
  ${breakpoint(`mobile`)} {
    font-size: 12px;
    margin-left: 10px;
  }
`;
const FormModify = styled.button`
  line-height: 1;
  text-decoration: underline !important;
  color: #828385;
  font-size: 16px;
  margin-left: auto;
  ${breakpoint(`tablet`)} {
    font-size: 14px;
  }
`;

type SubmitParams = Parameters<
  typeof PaymentControllerService.paymentInfoSetUsingPost
>[0];

// 후원모듈에서 넘어올시 가져와지는 데이터
export interface SupportModuleInputData extends SupportInitialValues {
  moduleCode: string;
  solicitCode: string;
  sessionId?: string;
  moduleInfo: {
    optSupportAlert: string;
    optBannerList: [BannerInfoData];
  };
  campaignName?: string;
}

// 리워드 캠페인 옴션 데이터 (후원모듈을 사용하지 않는 캠페인)
export interface SupportOptionData extends SupportInitialValues {
  sdoCode?: string;
  sessionId?: string;
  supportTerm?: string;
  supportCategory?: string;
  solicitCode?: string;
  solicitName?: string; // codebase
  solicitName2?: string; // weaction
  supportAmount1?: number;
  supportAmount2?: number;
  supportAmount3?: number;
  supportAmount4?: number;
  supportAmount5?: number;
  minAmount?: number; // 최소 후원 금액. 설정이 없을 경우 -1
}

const DonatePage: FC<PageProps> = observer(({ location }) => {
  const confPgType = process.env.PG_TYPE_DONATE || 'KCP'; // 후원하기 페이지용 PG사
  const isRegularKcpStandard = false; // 정기후원 KCP 통합 결제창 사용 여부 (202512 개발 완료하였으나 현업 요청으로 사용 중단)
  const userStore = useUserStore();
  const popupStore = usePopupStore();
  const locationTest = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [step1Data, setStep1Data] = useState<SupportStep1Data>();
  const [step2Data, setStep2Data] = useState<SupportStep2Data>();
  const [savedStep3Data, setSavedStep3Data] = useState<Partial<SupportStep3Data>>();
  const [regularPayInfo, setRegularPayInfo] = useState<RegularPayInfoVo>();
  const totalData = useMemo(() => ({ ...step1Data, ...step2Data }), [
    step1Data,
    step2Data,
  ]);
  const queryParams = useMemo(
    () => ({
      ...Object.fromEntries(new URLSearchParams(location.search)),
      // 같이 하는 기념기부
      ...location.state?.anniProjectData,
    }),
    [location.search, location.state],
  );

  // WEB-409: DM26A 캠페인 종료 — 후원코드 URL 접속 시 공지사항으로 리다이렉트
  useEffect(() => {
    if (queryParams.solicitcode === 'DM26A') {
      window.location.href = 'https://www.unicef.or.kr/about-us/notice/251416';
    }
  }, [queryParams.solicitcode]);

  console.log('+++ location', location);
  console.log('+++ queryParams', queryParams);
  console.log('+++ locationTest', locationTest);

  const inicisPcRef = useRef<InicisPcOnceRef>(null);
  const inicisMobileRef = useRef<InicisMobileOnceRef>(null);
  const kcpPcRef = useRef<KcpPcRef>(null);
  const kcpMobileRef = useRef<KcpMobileRef>(null);
  const naverRef = useRef<NaverPayRef>(null);
  const mobiliansRef = useRef<MobiliansOnceRef>(null);
  // 최종 전송 가능한 상태인지 여부
  const [isDataValid, setIsDataValid] = useState(false);
  // 후원 카테고리 관련
  const supportCategories = useSupportCategoriesForPayment();
  const selectedCategory = useMemo(
    () =>
      step1Data
        ? supportCategories.find(
            (category) =>
              category.indexNumber === Number(step1Data.supportCategoryIndex),
          )
        : null,
    [step1Data, supportCategories],
  );
  const [moduleData, setModuleData] = useState<SupportModuleInputData>(); // 후원모듈
  const [sdoData, setSdoData] = useState<SupportOptionData>(); // 리워드캠페인옵션 (후원모듈X)
  const [
    moduleBannerStep1,
    setModuleBannerStep1,
  ] = useState<BannerInfoData | null>();
  const [
    moduleBannerStep2,
    setModuleBannerStep2,
  ] = useState<BannerInfoData | null>();
  const [
    moduleBannerStep3,
    setModuleBannerStep3,
  ] = useState<BannerInfoData | null>();
  const [moduleLoaded, setModuleLoaded] = useState(false);
  // 캠페인명
  const [campaignName, setCampaignName] = useState();
  // 정기결제 방법 목록 사용여부 (현재는 로그인 여부만 조건이지만 추후 변동될 경우 대비 변수 처리)
  const [isUsePaymentMadateList, setIsUsePaymentMadateList] = useState(false);

  // 총 후원금액
  const finalAmount = useMemo(() => {
    const { supportAmount, supportAmountInput } = totalData;
    return Number(supportAmount || supportAmountInput || 0);
  }, [totalData]);

  // 현금영수증 신청 여부
  const isRequestReceipt = (param: any) => {
    if (
      param?.supporterType === ENUM_SUPPORTER_TYPE.person &&
      param?.agreeReceipt === true
    ) {
      return true;
    } else {
      return false;
    }
  };

  // 완료 페이지
  const completeUrl = `${process.env.SELF_URL}/donate/complete/`;

  // WEB-327 후원 완료 전환 로딩 오버레이
  const [processStage, setProcessStage] = useState<DonateProcessStage | null>(
    null,
  );
  const processTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const clearProcessTimers = useCallback(() => {
    processTimersRef.current.forEach((timer) => clearTimeout(timer));
    processTimersRef.current = [];
  }, []);
  // 제출 시작: 1단계(결제 확인) 표시 후 일정 시간 뒤 2단계(후원 등록)로 자동 진행
  const openProcessOverlay = useCallback(() => {
    clearProcessTimers();
    setProcessStage(`confirm`);
    processTimersRef.current.push(
      setTimeout(() => setProcessStage(`register`), 1400),
    );
  }, [clearProcessTimers]);
  const closeProcessOverlay = useCallback(() => {
    clearProcessTimers();
    setProcessStage(null);
  }, [clearProcessTimers]);
  // 등록 완료 응답 수신: 3단계(거의 다 됐어요) -> 완료(3/3 원) -> 완료 체크카드 재생 후 완료 페이지로 이동
  // WEB-327: almost -> done(3/3 원) -> navigate(원형 체크) -> 완료 페이지 이동
  const completeProcessOverlay = useCallback(
    (url: string, orderNumber: string | undefined) => {
      clearProcessTimers();
      setProcessStage(`almost`);
      processTimersRef.current.push(
        setTimeout(() => setProcessStage(`done`), 1100),
        // done(3/3 원) 노출 후 체크카드로 전환
        setTimeout(() => setProcessStage(`navigate`), 2100),
        // 체크 그리기 애니메이션이 끝까지 보이도록 최소 ~1.2초 확보 후 이동
        setTimeout(() => PaymentHelper.onComplete(url, orderNumber), 3300),
      );
    },
    [clearProcessTimers],
  );
  useEffect(() => () => clearProcessTimers(), [clearProcessTimers]);
  // 오류 알럿이 뜨면 로딩 오버레이 닫기 (제출 중 오류 경로 공통 처리)
  // 완료 시퀀스(almost/done) 진입 후에는 완료 페이지 이동 타이머를 취소하지 않도록 제외
  useEffect(() => {
    if (
      popupStore.isOpen &&
      (processStage === `confirm` || processStage === `register`)
    ) {
      closeProcessOverlay();
    }
  }, [popupStore.isOpen, processStage, closeProcessOverlay]);

  // Step1 요약 텍스트
  const step1SummaryText = useMemo(() => {
    if (step1Data) {
      const { supportTerm } = step1Data;

      return [
        selectedCategory?.codeName,
        finalAmount.toLocaleString(),
        SUPPORT_TERMS[supportTerm],
      ].join(` / `);
    }
    return null;
  }, [finalAmount, step1Data, selectedCategory]);

  // Step2 요약 텍스트
  const step2SummaryText = useMemo(() => {
    if (step2Data) {
      const {
        supporterType,
        memberTypeCode,
        memberName,
        birthday,
        businessNumber,
      } = step2Data;
      const memberType =
        supporterType === ENUM_SUPPORTER_TYPE.person
          ? MEMBER_TYPE_INDIVIDUAL
          : memberTypeCode;
      return (
        [
          MEMBER_TYPE_FLATMAP[memberType],
          memberName,
          supporterType === ENUM_SUPPORTER_TYPE.person
            ? birthday
            : businessNumber,
        ]
          // 단체의 경우, 사업자번호가 없기 때문에 비워둠
          .filter((v) => v)
          .join(` / `)
      );
    }
    return null;
  }, [step2Data]);

  // step3 완료시
  const onSubmit = useCallback(
    async (step3Data: SupportStep3Data) => {
      // beforeunload 해제
      setIsDataValid(true);
      const finalData = { ...totalData, ...step3Data };
      // WEB-327 로딩 오버레이 제외: 네이버페이(정기/일시), 일시후원 신용카드 — 외부 결제창으로 바로 이동
      // 기등록 결제수단(mandateId)은 서버에서 직접 처리하므로 오버레이 표시
      const skipOverlay =
        !finalData.mandateId &&
        (finalData.paymentType === PAYTYPE_ONCE_NAVERPAY ||
          finalData.paymentType === PAYTYPE_REGULAR_NAVERPAY ||
          (finalData.supportTerm === SUPPORT_TERM_ONCE &&
            finalData.paymentType === PAYTYPE_ONCE_CARD));
      if (!skipOverlay) {
        openProcessOverlay();
      }
      const finalSupportCategory = supportCategories.find(
        (category) =>
          category.indexNumber === Number(finalData.supportCategoryIndex),
      );

      const encKey = getDonateEncKey();
      // 기부금영수증 신청 + 실명인증 완료 상태에서 암호화키가 만료된 경우 제출 차단
      // 단, 로그인 회원 중 주민번호가 이미 등록된 경우는 암호화키 없이 통과
      if (
        isRequestReceipt(finalData) &&
        !encKey &&
        !(userStore.user && userStore.isValidJuminno())
      ) {
        alert(
          '보안 인증 시간이 만료되었습니다.\n페이지를 새로고침 후 다시 시도해 주세요.',
        );
        setIsDataValid(false);
        return;
      }
      const finalParams: SubmitParams = {
        // Step1
        supportTypeCode:
          finalData.supportTerm === SUPPORT_TERM_ONCE
            ? SUPPORT_TERM_ONCE
            : SUPPORT_TERM_REGULAR,
        donationCode: finalSupportCategory?.code || ``,
        solicitCode:
          queryParams.solicitcode ||
          moduleData?.solicitCode ||
          finalSupportCategory?.solicitCode ||
          ``,
        sfChannel: queryParams.sfchannel || ``,
        totalAmount: finalAmount,

        // Step2
        memberTypeCode:
          finalData.supporterType === ENUM_SUPPORTER_TYPE.person
            ? MEMBER_TYPE_INDIVIDUAL
            : finalData.memberTypeCode || ``,
        memberName: finalData.memberName,
        birthday: finalData.birthday,
        agreeReceiptYn: finalData.agreeReceipt ? `Y` : `N`,
        // 개인/단체 여부에 따라 가져오는 필드 분기
        juminNumber:
          finalData.supporterType === ENUM_SUPPORTER_TYPE.person
            ? finalData.agreeReceipt === true
              ? // 240627 - 보안점검 :주민등록번호 평문 > 암호화 데이터로 교체
                finalData.juminEnc || ''
              : ''
            : finalData.businessNumber || '',
        mobilePhoneNumber:
          finalData.supporterType === ENUM_SUPPORTER_TYPE.person
            ? finalData.mobilePhoneNumber || ''
            : finalData.contact || '',
        email:
          finalData.supporterType === ENUM_SUPPORTER_TYPE.person
            ? finalData.email || ''
            : finalData.managerEmail || '',
        businessNumber: finalData.businessNumber,
        managerName: finalData.managerName,
        managerEmail: finalData.managerEmail,
        mobileAgreeYn: finalData.mobileAgree ? `Y` : `N`,
        emailAgreeYn: finalData.emailAgree ? `Y` : `N`,
        postAgreeYn: finalData.postAgree ? `Y` : `N`,
        zipCode: finalData.zipCode || ``,
        address: finalData.address || ``,
        addressDetail: finalData.addressDetail,
        relationType: finalData.relationType,
        relationName: finalData.relationName,
        relationTelNumber: finalData.relationTelNumber,
        // 기념일 정보 추가
        anniType: finalData.anniType,
        anniDate: finalData.anniDate?.replace(/\./gi, ``),
        story: finalData.story,
        honor: finalData.honor,
        receiptEmailYn:
          finalData.memberTypeCode === MEMBER_TYPE_SCHOOL
            ? finalData.isReceiptEmail
              ? `Y`
              : `N`
            : '',
        receiptDmYn:
          finalData.memberTypeCode === MEMBER_TYPE_SCHOOL
            ? finalData.isReceiptDm
              ? `Y`
              : `N`
            : '',
        receiptFaxYn:
          finalData.memberTypeCode === MEMBER_TYPE_SCHOOL
            ? finalData.isReceiptFax
              ? `Y`
              : `N`
            : '',
        receiptFaxNumber:
          (finalData.memberTypeCode === MEMBER_TYPE_SCHOOL &&
            finalData.isReceiptFax) === true
            ? finalData.receiptFaxNumber
            : '',
        // Step3
        paymentTypeCode: `${finalData.paymentType}`,
        acsType: finalData.paymentBankingAgreeType,
        signBinary: finalData.signBinary,
        nmcomp: finalData.bankingOwnerName,
        cardOwnerName: finalData.cardOwnerName,
        depositorBirthday:
          finalData.paymentType === PAYTYPE_REGULAR_CARD
            ? finalData.cardOwnerBirthday
            : finalData.bankingOwnerBirthday,
        relation: finalData.bankingOwnerRelation,
        depositorMobilePhoneNumber: finalData.bankingOwnerPhone,
        banksett: finalData.paymentBankCode,
        noacct: finalData.bankingNumber,
        paymentDay: finalData.paymentDay,
        cardNumber:
          finalData.paymentType === PAYTYPE_REGULAR_CARD
            ? `${finalData.cardNumber1}${finalData.cardNumber2}${finalData.cardNumber3}${finalData.cardNumber4}`
            : ``,
        cardExpire:
          finalData.paymentType === PAYTYPE_REGULAR_CARD
            ? `${finalData.cardExpireY}${finalData.cardExpireM}`
            : ``,

        // etc
        productName: getSupportProductName(finalSupportCategory),
        deviceType: isMobileDevice() ? `MOBILE` : `PC`,
        nationCode: `KR`,
        pathType: `SUPPORT`,
        moduleCode: moduleData?.moduleCode ? moduleData.moduleCode : `EMPTY`,
        sessionId: moduleData?.sessionId || sdoData?.sessionId || undefined,
        sdoCode: sdoData?.sdoCode ? sdoData.sdoCode : `EMPTY`,
        successUrl: `${completeUrl}`,

        // trackcode 저장
        trackcode: SessionStorage.getItem(`trackcode`) || undefined,
        trk_id: SessionStorage.getItem(`trk_id`) || undefined,

        // 기업회원 누락으로 추가
        contact: finalData.contact,

        // 같이 하는 기념기부
        aprjtIdx: queryParams.aprjtIdx,
        openNameYn: queryParams.openNameYn,
        openAmountYn: queryParams.openAmountYn,

        // 개인정보 암호화
        encCert: isRequestReceipt(finalData) === true && encKey?.cert,
        publicKey: isRequestReceipt(finalData) === true && encKey?.key,
        /*
        isSecureContext:
          isRequestReceipt(finalData) === true && encKey?.isSecureContext,
        */
        secureContextYn:
          isRequestReceipt(finalData) === true && encKey?.isSecureContext
            ? 'Y'
            : 'N',
        // PG 변경에 따른 값 추가
        pgType: getPgType(confPgType, `${finalData.paymentType}`),
        // 결제 목록 코드
        mandateId: finalData.mandateId,
        paymentKey: finalData.paymentKey,
        memberId: finalData.memberId,
        useBillPaymentYn:
          confPgType === 'KCP' && !isRegularKcpStandard ? 'Y' : 'N',
        // 세일즈도입 후 추가된 트래킹코드
        utmCampaign: SessionStorage.getItem(`utm_campaign`) || '',
        utmSource: SessionStorage.getItem(`utm_source`) || '',
        utmMedium: SessionStorage.getItem(`utm_medium`) || '',
        utmContent: SessionStorage.getItem(`utm_content`) || '',
        utmTerm: SessionStorage.getItem(`utm_term`) || '',
        digitalMaterial: SessionStorage.getItem(`digital_material`) || '',
      };

      const currentSupportCategory = supportCategories.find(
        (category) =>
          category.indexNumber === Number(step1Data?.supportCategoryIndex),
      );

      // 주문정보
      const orderData = {
        campaignName:
          campaignName ||
          (Object.keys(queryParams).length === 0 ? 'direct' : 'none'),
        campaignPagenum:
          moduleData?.solicitCode || queryParams.solicitcode || '',
        promotionName:
          campaignName ||
          (Object.keys(queryParams).length === 0 ? 'direct' : 'none'),
        promotionId: moduleData?.solicitCode || queryParams.solicitcode || '',
        value: finalData?.supportAmount || finalData?.supportAmountInput || '0',
        items: [
          {
            itemId: currentSupportCategory?.code,
            itemName: currentSupportCategory?.codeName,
            itemCategory: currentSupportCategory?.categoryCode,
            itemCategory2:
              finalData.birthday &&
              dayjs(finalData.birthday, `YYYYMMDD`, true).isValid()
                ? `${finalData.birthday.slice(0, 4)}`
                : '',
            itemCategory3: finalData.gender || '',
            itemListName: '',
            quantity: '1',
            price:
              finalData?.supportAmount || finalData?.supportAmountInput || '0',
          },
        ],
        paymentType: finalParams?.paymentTypeCode,
      };
      // gtm addPaymentInfo
      GtmHelper.addPaymentInfo(orderData);

      // 폼데이터로 변환
      // const formData = new FormData();
      // Object.keys(finalParams).forEach(
      //   (key) =>
      //     finalParams[key] !== undefined &&
      //     formData.append(key, finalParams[key]),
      // );
      const formData = finalParams;

      // 차단 후원자 검증
      try {
        const { data: validateResult } = await axios.post(
          `${process.env.BACKEND_URL}/unicef/api/fo/common/validateDonor`,
          null,
          {
            params: {
              donorName: finalParams.memberName,
              birthday: finalParams.birthday || '',
            },
          },
        );
        if (validateResult.resultCode === `blocked`) {
          popupStore.show(
            validateResult.resultMessage ||
              `후원 처리가 불가합니다.\n후원본부(02-737-1004)로 연락주세요.`,
          );
          return;
        }
      } catch (e) {
        // 검증 API 실패 시 후원 진행 허용
      }

      // 전송 먼저
      // swagger에는 파라미터가 쿼리스트링으로 되어있어서 maxLength 오류남. FormData로 전송을 위해 axios 사용.
      try {
        const {
          data: {
            resultCode,
            resultMessage,
            orderNumber,
            mkey,
            signature,
            timestamp,
            reserveId,
            orderToken,
            productCode,
            productName,
            totalPayAmount,
            returnUrl,
            actionType,
            targetRecurrentId,
            kcpInfo,
          },
        } = await axios.post<PaymentInfoSetVo>(
          `${process.env.BACKEND_URL}/unicef/api/fo/common/payment/paymentInfoSet`,
          formData,
          {
            withCredentials: true,
            data: formData,
          },
        );

        // 주문번호 생성에 성공하면
        if (resultCode === `success`) {
          // 주문정보 저장 (리다이렉트 시 sessionStorage 유실 대비 localStorage 사용)
          LocalStorage.setItem(`orderData_${orderNumber}`, JSON.stringify(orderData));
          // 주문토큰 저장
          SessionStorage.setItem(`orderToken`, orderToken || '');
          // trackcode, trk_id 비우기
          SessionStorage.removeItem(`trackcode`);
          SessionStorage.removeItem(`trk_id`);
          SESSION_STORAGE_KEYS__DONATE_TRACKING_CODE.forEach((key) => {
            SessionStorage.removeItem(key);
          });
          // 개인정보 암호화키 초기화 (완료)
          resetDonateEncKey();

          // 일시후원
          if (finalData.supportTerm === SUPPORT_TERM_ONCE) {
            // KCP 가상계좌 HUB API 직접 호출 (팝업 없음)
            if (
              finalData.paymentType === PAYTYPE_ONCE_BANKING &&
              confPgType === 'KCP'
            ) {
              try {
                const vaRes = await axios.post(
                  `${process.env.BACKEND_URL}/unicef/api/fo/common/payment/reqKcpVcntHub`,
                  {
                    ordr_idxx: orderNumber,
                    amount: String(finalParams?.totalAmount || 0),
                    good_name: finalParams?.productName || '',
                    buyr_name: finalParams?.memberName || '',
                    buyr_tel2: finalData.mobilePhoneNumber || '',
                    buyr_mail: finalParams?.email || '',
                    va_bankcode: KCP_BANK_MAP[finalData.vaBankCode || ''],
                    bank_code: finalData.vaBankCode || '', // SF 전달용 표준 금융기관코드
                    va_name: finalData.vaName || finalParams?.memberName || '',
                    va_date: getKcpVaDate(3),
                  },
                  {
                    withCredentials: true,
                  },
                );
                if (vaRes.data.resultCode === 'success') {
                  SessionStorage.setItem(
                    'vcntResult',
                    JSON.stringify(vaRes.data.data),
                  );
                  completeProcessOverlay(completeUrl, orderNumber);
                } else {
                  popupStore.show(
                    vaRes.data.resultMessage || '가상계좌 발급에 실패했습니다.',
                  );
                }
              } catch (e) {
                closeProcessOverlay();
                if (e.response) {
                  popupStore.show(
                    e.response.data?.resultMessage ||
                      '가상계좌 발급에 실패했습니다.',
                  );
                }
              }
              return;
            }
            // PG 결제창으로 진행하므로 처리 오버레이 닫기 (WEB-327)
            closeProcessOverlay();
            // PG 일시후원일 경우
            if (
              [PAYTYPE_ONCE_CARD, PAYTYPE_ONCE_BANKING].includes(
                `${finalData.paymentType}`,
              )
            ) {
              // 모바일이면
              if (isMobileDevice()) {
                if (confPgType === 'KCP') {
                  if (
                    !orderNumber ||
                    !kcpInfo?.siteId ||
                    !kcpInfo?.siteName ||
                    !kcpInfo?.approvalKey ||
                    !kcpInfo?.traceNo ||
                    !kcpInfo?.payUrl ||
                    !kcpInfo?.retUrl
                  ) {
                    popupStore.show(`주요정보가 넘어오지 않았습니다`);
                    return;
                  }
                  kcpMobileRef.current?.init({
                    pgProc: 'pay',
                    siteCd: kcpInfo?.siteId,
                    siteName: kcpInfo?.siteName,
                    siteLogo: kcpInfo?.siteLogo || '',
                    approvalKey: kcpInfo?.approvalKey,
                    traceNo: kcpInfo?.traceNo,
                    payUrl: kcpInfo?.payUrl,
                    retUrl: kcpInfo?.retUrl,
                    payMethod: KCP_PAY_METHOD_MOBILE_MAP[finalData.paymentType],
                    goodname: finalParams?.productName || '',
                    buyername: finalParams?.memberName || '',
                    buyeremail:
                      (finalData.supporterType === ENUM_SUPPORTER_TYPE.group
                        ? finalData.managerEmail
                        : finalParams?.email) || '',
                    buyertel:
                      (finalData.supporterType === ENUM_SUPPORTER_TYPE.group
                        ? finalData.contact
                        : finalData.mobilePhoneNumber) || ``,
                    price: finalParams?.totalAmount || 0,
                    oid: orderNumber,
                    completeUrl: finalParams?.successUrl,
                    failUrl: window.location.href,
                    gender: '',
                    missModuleCode: '',
                    missUserId: '',
                    solicitCode: '',
                    currency: '',
                  });
                } else {
                  if (!orderNumber) {
                    popupStore.show(`주요정보가 넘어오지 않았습니다`);
                    return;
                  }
                  inicisMobileRef.current?.init({
                    payMethod:
                      INICIS_PAY_METHOD_MOBILE_MAP[finalData.paymentType],
                    goods: finalParams?.productName.replace(/&/gi, `_`),
                    uname: (finalParams?.memberName || '').replace(/&/gi, `_`),
                    amt: finalParams?.totalAmount || 0,
                    oid: orderNumber,
                    missUserId: '',
                    solicitCode: '',
                  });
                }
              } else {
                if (confPgType === 'KCP') {
                  if (
                    !orderNumber ||
                    !kcpInfo?.siteId ||
                    !kcpInfo?.siteName ||
                    !kcpInfo?.jsPlugin
                  ) {
                    popupStore.show(`주요정보가 넘어오지 않았습니다`);
                    return;
                  }
                  kcpPcRef.current?.init({
                    pgProc: 'pay',
                    siteCd: kcpInfo?.siteId,
                    siteName: kcpInfo?.siteName,
                    siteLogo: kcpInfo?.siteLogo || '',
                    jsPluginUrl: kcpInfo?.jsPlugin,
                    payMethod: KCP_PAY_METHOD_PC_MAP[finalData.paymentType],
                    goodname: finalParams?.productName || '',
                    buyername: finalParams?.memberName || '',
                    buyeremail:
                      (finalData.supporterType === ENUM_SUPPORTER_TYPE.group
                        ? finalData.managerEmail
                        : finalParams?.email) || '',
                    buyertel:
                      (finalData.supporterType === ENUM_SUPPORTER_TYPE.group
                        ? finalData.contact
                        : finalData.mobilePhoneNumber) || ``,
                    price: finalParams?.totalAmount || 0,
                    oid: orderNumber,
                    completeUrl: finalParams?.successUrl,
                    failUrl: window.location.origin,
                    gender: '',
                    missModuleCode: '',
                    missUserId: '',
                    solicitCode: '',
                    currency: '',
                  });
                } else {
                  if (!orderNumber || !mkey || !signature || !timestamp) {
                    popupStore.show(`주요정보가 넘어오지 않았습니다`);
                    return;
                  }
                  inicisPcRef.current?.init({
                    payMethod: INICIS_PAY_METHOD_PC_MAP[finalData.paymentType],
                    goodname: finalParams?.productName,
                    buyername: finalParams?.memberName,
                    buyeremail:
                      finalData.supporterType === ENUM_SUPPORTER_TYPE.group
                        ? finalData.managerEmail
                        : finalParams?.email,
                    buyertel:
                      finalData.supporterType === ENUM_SUPPORTER_TYPE.group
                        ? finalData.contact
                        : finalData.mobilePhoneNumber || ``,
                    price: finalParams?.totalAmount || 0,
                    mKey: mkey,
                    oid: orderNumber,
                    signature,
                    timestamp,
                    gender: '',
                    missModuleCode: '',
                    missUserId: '',
                    solicitCode: '',
                  });
                }
              }
            }
            // 네이버 결제
            else if (finalData.paymentType === PAYTYPE_ONCE_NAVERPAY) {
              if (!orderNumber) {
                popupStore.show(`주요정보가 넘어오지 않았습니다`);
                return;
              }
              naverRef.current?.payNormal({
                productCount: 1,
                productName: finalParams?.productName || '',
                totalPayAmount: finalParams?.totalAmount || 0,
                taxScopeAmount: 0,
                taxExScopeAmount: finalParams?.totalAmount || 0,
                merchantPayKey: orderNumber,
                merchantUserKey: finalParams?.memberName?.replaceAll(
                  /\s/g,
                  '_',
                ),
              });
            }
            // 모빌리언스 휴대폰 결제
            else if (finalData.paymentType === PAYTYPE_ONCE_PHONE) {
              if (!orderNumber) {
                popupStore.show(`주요정보가 넘어오지 않았습니다`);
                return;
              }
              mobiliansRef.current?.init({
                goodname: finalParams?.productName,
                price: finalParams?.totalAmount || 0,
                oid: orderNumber,
              });
            }
          }
          // 정기후원
          else if (finalData.supportTerm === SUPPORT_TERM_REGULAR) {
            if (isUsePaymentMadateList === true && finalData.mandateId) {
              // 결제 목록에서 선택한 경우
              completeProcessOverlay(`${completeUrl}`, orderNumber);
            } else {
              // 카드결제
              if (finalData.paymentType === PAYTYPE_REGULAR_CARD) {
                if (confPgType === 'KCP' && isRegularKcpStandard) {
                  // PG 결제창으로 진행하므로 처리 오버레이 닫기 (WEB-327)
                  closeProcessOverlay();
                  // 인증 배치키 생성 방식
                  // 모바일이면
                  if (isMobileDevice()) {
                    if (
                      !orderNumber ||
                      !kcpInfo?.siteId ||
                      !kcpInfo?.siteName ||
                      !kcpInfo?.approvalKey ||
                      !kcpInfo?.traceNo ||
                      !kcpInfo?.payUrl ||
                      !kcpInfo?.retUrl ||
                      !kcpInfo?.groupId ||
                      !kcpInfo?.paymentMethod
                    ) {
                      popupStore.show(`주요정보가 넘어오지 않았습니다`);
                      return;
                    }
                    kcpMobileRef.current?.init({
                      pgProc: 'bill',
                      siteCd: kcpInfo?.siteId,
                      siteName: kcpInfo?.siteName,
                      siteLogo: kcpInfo?.siteLogo || '',
                      groupId: kcpInfo?.groupId || '',
                      approvalKey: kcpInfo?.approvalKey,
                      traceNo: kcpInfo?.traceNo,
                      payUrl: kcpInfo?.payUrl,
                      retUrl: kcpInfo?.retUrl,
                      payMethod: kcpInfo.paymentMethod,
                      goodname: finalParams?.productName || '',
                      buyername: finalParams?.memberName || '',
                      buyeremail:
                        (finalData.supporterType === ENUM_SUPPORTER_TYPE.group
                          ? finalData.managerEmail
                          : finalParams?.email) || '',
                      buyertel:
                        (finalData.supporterType === ENUM_SUPPORTER_TYPE.group
                          ? finalData.contact
                          : finalData.mobilePhoneNumber) || ``,
                      price: finalParams?.totalAmount || 0,
                      oid: orderNumber,
                      completeUrl: finalParams?.successUrl,
                      failUrl: window.location.href,
                      gender: '',
                      missModuleCode: '',
                      missUserId: '',
                      solicitCode: '',
                      currency: '',
                    });
                  } else {
                    if (
                      !orderNumber ||
                      !kcpInfo?.siteId ||
                      !kcpInfo?.siteName ||
                      !kcpInfo?.groupId ||
                      !kcpInfo?.jsPlugin
                    ) {
                      popupStore.show(`주요정보가 넘어오지 않았습니다`);
                      return;
                    }
                    kcpPcRef.current?.init({
                      pgProc: 'bill',
                      siteCd: kcpInfo?.siteId,
                      siteName: kcpInfo?.siteName,
                      siteLogo: kcpInfo?.siteLogo || '',
                      groupId: kcpInfo?.groupId || '',
                      jsPluginUrl: kcpInfo?.jsPlugin,
                      payMethod: KCP_PAY_METHOD_PC_MAP[finalData.paymentType],
                      goodname: finalParams?.productName || '',
                      buyername: finalParams?.memberName || '',
                      buyeremail:
                        (finalData.supporterType === ENUM_SUPPORTER_TYPE.group
                          ? finalData.managerEmail
                          : finalParams?.email) || '',
                      buyertel:
                        (finalData.supporterType === ENUM_SUPPORTER_TYPE.group
                          ? finalData.contact
                          : finalData.mobilePhoneNumber) || ``,
                      price: finalParams?.totalAmount || 0,
                      oid: orderNumber,
                      completeUrl: finalParams?.successUrl,
                      failUrl: window.location.origin,
                      gender: '',
                      missModuleCode: '',
                      missUserId: '',
                      solicitCode: '',
                      currency: '',
                    });
                  }
                } else if (confPgType === 'KCP' && !isRegularKcpStandard) {
                  // 비인증 배치키 생성 방식
                  completeProcessOverlay(`${completeUrl}`, orderNumber);
                }
                return;
              }
              // 네이버페이
              if (finalData.paymentType === PAYTYPE_REGULAR_NAVERPAY) {
                // 네이버페이 결제창으로 진행하므로 처리 오버레이 닫기 (WEB-327)
                closeProcessOverlay();
                if (!reserveId) {
                  popupStore.show(`주요정보가 넘어오지 않았습니다`);
                  return;
                }

                naverRef.current?.payRecurrent({
                  actionType,
                  targetRecurrentId,
                  productCode,
                  productName,
                  totalPayAmount,
                  returnUrl: `${process.env.BACKEND_URL}/unicef/api/fo/common/payment/naverRegularReturn?orderNumber=${orderNumber}`,
                });
              }
              // 자동이체, 카드 (서버에서 전부 처리)
              else if (
                [PAYTYPE_REGULAR_AUTO_BANKING, PAYTYPE_REGULAR_CARD].includes(
                  `${finalData?.paymentType}`,
                )
              ) {
                // 바로 이동
                completeProcessOverlay(`${completeUrl}`, orderNumber);
              }
            }
          }
        }
        // 주문번호 생성에 실패하면
        else {
          closeProcessOverlay();
          if (resultMessage) {
            popupStore.show(resultMessage);
          }
        }
      } catch (e) {
        closeProcessOverlay();
        if (e.response) {
          popupStore.show(e.response.data.resultMessage);
          console.error(e);
        }
      }
    },
    [
      totalData,
      supportCategories,
      queryParams,
      moduleData?.solicitCode,
      moduleData?.moduleCode,
      finalAmount,
      campaignName,
      step1Data?.supportCategoryIndex,
      popupStore,
      openProcessOverlay,
      closeProcessOverlay,
      completeProcessOverlay,
    ],
  );
  const scrollToTop = (step: number) => {
    //window.scrollTo(0, 0);
    // ref 사용시 유동적 계산 단계가 더 많아 고정값 입력
    let sectionTop: number = 0;
    const width = window.innerWidth;
    // 상태별 해상도별 위치가 달라서 수동 코딩 진행
    if (step === 1) {
      if (Breakpoints['mobile'] >= width) {
        sectionTop = 195;
      } else if (
        Breakpoints['mobile'] < width &&
        Breakpoints['tablet'] >= width
      ) {
        sectionTop = 200;
      } else {
        sectionTop = 240;
      }
    } else if (step === 2) {
      if (Breakpoints['mobile'] >= width) {
        sectionTop = 250;
      } else if (
        Breakpoints['mobile'] < width &&
        Breakpoints['tablet'] >= width
      ) {
        sectionTop = 275;
      } else {
        sectionTop = 300;
      }
    } else if (step === 3) {
      if (Breakpoints['mobile'] >= width) {
        sectionTop = 310;
      } else if (
        Breakpoints['mobile'] < width &&
        Breakpoints['tablet'] >= width
      ) {
        sectionTop = 340;
      } else {
        sectionTop = 371;
      }
    }
    //// 후원모듈 step1배너 상시 노출시 높이값 변동
    if (moduleData && moduleBannerStep1) {
      if (Breakpoints['mobile'] >= width) {
        sectionTop += 30;
      } else if (
        Breakpoints['mobile'] < width &&
        Breakpoints['tablet'] >= width
      ) {
        sectionTop += 40;
      } else {
        sectionTop += 35;
      }
    }
    if (sectionTop > 0) {
      window.scrollTo({
        top: sectionTop,
        behavior: 'auto',
      });
    }
  };

  const onStep1Submit = useCallback(
    (data: SupportStep1Data) => {
      const currentSupportCategory = supportCategories.find(
        (category) =>
          category.indexNumber === Number(data.supportCategoryIndex),
      );
      // 캠페인에서 넘어올 시 캠페인명 로드까지 완료 후 gtm 전송
      if (currentSupportCategory && moduleLoaded) {
        // gtm beginCheckout
        GtmHelper.beginCheckout({
          campaignName:
            campaignName ||
            (Object.keys(queryParams).length === 0 ? 'direct' : 'none'),
          campaignPagenum:
            moduleData?.solicitCode || queryParams.solicitcode || '',
          promotionName:
            campaignName ||
            (Object.keys(queryParams).length === 0 ? 'direct' : 'none'),
          promotionId: moduleData?.solicitCode || queryParams.solicitcode || '',
          value: data.supportAmount || data.supportAmountInput || '0',
          items: [
            {
              itemId: currentSupportCategory.code,
              itemName: currentSupportCategory.codeName,
              itemCategory: currentSupportCategory.categoryCode,
              itemCategory2: '',
              itemCategory3: '',
              itemListName: '',
              quantity: '1',
              price: data.supportAmount || data.supportAmountInput || '0',
            },
          ],
        });
      }

      setStep1Data(data);
      setCurrentStep(2);
      scrollToTop(2);
    },
    [
      campaignName,
      moduleData?.solicitCode,
      moduleLoaded,
      queryParams,
      supportCategories,
    ],
  );

  const onStep2Submit = useCallback(
    (data: SupportStep2Data) => {
      const currentSupportCategory = supportCategories.find(
        (category) =>
          category.indexNumber === Number(step1Data?.supportCategoryIndex),
      );
      // gtm addDonorInfo
      GtmHelper.addDonorInfo({
        campaignName:
          campaignName ||
          (Object.keys(queryParams).length === 0 ? 'direct' : 'none'),
        campaignPagenum:
          moduleData?.solicitCode || queryParams.solicitcode || '',
        promotionName:
          campaignName ||
          (Object.keys(queryParams).length === 0 ? 'direct' : 'none'),
        promotionId: moduleData?.solicitCode || queryParams.solicitcode || '',
        value: step1Data?.supportAmount || step1Data?.supportAmountInput || '0',
        items: [
          {
            itemId: currentSupportCategory?.code,
            itemName: currentSupportCategory?.codeName,
            itemCategory: currentSupportCategory?.categoryCode,
            itemCategory2:
              data.birthday &&
              dayjs(`${data.birthday}`, `YYYYMMDD`, true).isValid()
                ? `${data.birthday.slice(0, 4)}`
                : ``,
            itemCategory3: data.gender || '',
            itemListName: '',
            quantity: '1',
            price:
              step1Data?.supportAmount || step1Data?.supportAmountInput || '0',
          },
        ],
      });

      setStep2Data(data);
      setCurrentStep(3);
      scrollToTop(3);
    },
    [
      campaignName,
      moduleData?.solicitCode,
      queryParams,
      step1Data?.supportAmount,
      step1Data?.supportAmountInput,
      step1Data?.supportCategoryIndex,
      supportCategories,
    ],
  );

  // 후원모듈 정보 가져오기 (로컬호스트에선 작동 안됨 - 파라미터를 쿠키로 관리)
  const loadSupportModuleData = useCallback(async () => {
    try {
      let bannerDataStep1 = null;
      let bannerDataStep2 = null;
      let bannerDataStep3 = null;

      if (queryParams.moduleCode) {
        // 후원모듈 정보 조회
        const {
          data,
          resultCode,
        } = await SupportModuleControllerService.getSupportModuleInputInfoUsingGet(
          queryParams,
        );
        if (resultCode === `success`) {
          const newData = data as SupportModuleInputData;
          setModuleData(newData);
          //// 후원모듈 배너 가져오기
          if (data && newData.moduleInfo?.optBannerList) {
            // step1
            const bannerStep1 = newData.moduleInfo?.optBannerList.filter(
              (el) => el.bnDispLoc === BANNER_DISP_LOC_CD__DONATE_S1,
            );
            bannerDataStep1 = bannerStep1.length > 0 ? bannerStep1[0] : null;
            // step2
            const bannerStep2 = newData.moduleInfo?.optBannerList.filter(
              (el) => el.bnDispLoc === BANNER_DISP_LOC_CD__DONATE_S2,
            );
            bannerDataStep2 = bannerStep2.length > 0 ? bannerStep2[0] : null;
            // step3
            const bannerStep3 = newData.moduleInfo?.optBannerList.filter(
              (el) => el.bnDispLoc === BANNER_DISP_LOC_CD__DONATE_S3,
            );
            bannerDataStep3 = bannerStep3.length > 0 ? bannerStep3[0] : null;
          }

          // 후원 category_name 불러오기
          try {
            const {
              data: _data,
            } = await SupportModuleControllerService.getCampaignNameUsingGet({
              solicitCode: data?.solicitCode,
            });
            setCampaignName(_data?.campaignName);
          } catch (e) {
            console.error(e);
          }
        }
      } else if (queryParams.sdoCode) {
        // 리워드 캠페인 옵션 저장 정보 조회 (후원모듈을 사용하지 않는 캠페인)
        const {
          data,
          resultCode,
        } = await SupportModuleControllerService.getSupportOptionInfo(
          queryParams,
        );
        if (resultCode === `success`) {
          if (data) {
            const newData = data as SupportOptionData;
            setSdoData(newData);
            // 캠페인명 셋팅
            setCampaignName(newData?.solicitName);
            // 후원코드 정보 로딩
            queryParams['supportTerm'] = newData.supportTerm;
            queryParams['supportCategory'] = newData.supportCategory;
            queryParams['solicitcode'] = newData.solicitCode;
          }
        }
      }

      //// 후원코드 배너 가져오기
      const {
        data: bannerData,
        resultCode: bnResultCode,
      } = await SupportModuleControllerService.getSupportCodeBannerListUsingGet(
        queryParams,
      );
      if (bnResultCode === `success` && bannerData) {
        const optBannerList = bannerData.data as [BannerInfoData];
        // 후원유도배너 셋팅
        // step1
        if (bannerDataStep1 === null) {
          const bannerStep1 = optBannerList.filter(
            (el) => el.bnDispLoc === BANNER_DISP_LOC_CD__DONATE_S1,
          );
          bannerDataStep1 = bannerStep1.length > 0 ? bannerStep1[0] : null;
        }
        // step2
        if (bannerDataStep2 === null) {
          const bannerStep2 = optBannerList.filter(
            (el) => el.bnDispLoc === BANNER_DISP_LOC_CD__DONATE_S2,
          );
          bannerDataStep2 = bannerStep2.length > 0 ? bannerStep2[0] : null;
        }
        // step3
        if (bannerDataStep3 === null) {
          const bannerStep3 = optBannerList.filter(
            (el) => el.bnDispLoc === BANNER_DISP_LOC_CD__DONATE_S3,
          );
          bannerDataStep3 = bannerStep3.length > 0 ? bannerStep3[0] : null;
        }
      }
      setModuleBannerStep1(bannerDataStep1);
      setModuleBannerStep2(bannerDataStep2);
      setModuleBannerStep3(bannerDataStep3);
    } catch (e) {}
    // 모듈 로드 완료
    setModuleLoaded(true);
  }, []);

  //후원 진입시 step1이동
  const isInitialLoad = useRef(true); // 최초 진입 여부
  const initFocus = (node: any) => {
    // ✅ 최초 진입일 때만 실행
    if (isInitialLoad.current && node) {
      scrollToTop(currentStep);

      // 최초 진입 이후엔 false로 바꿔서 이후 스크롤 안 되게
      isInitialLoad.current = false;
    }
  };

  const [isSticky, setSticky] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const handleScroll = throttle(() => {
    if (containerRef.current) {
      const { top } = containerRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth <= 640;
      setSticky(isMobile ? top <= 56 : top <= 120);
    }
  }, 20);

  // data handler
  useEffect(() => {
    loadSupportModuleData();
    // 개인정보 암호화키 초기화 (진입)
    resetDonateEncKey();
  }, [loadSupportModuleData]);

  // scroll handler (data handler랑 병합 금지!!!)
  useEffect(() => {
    // 후원 단계 스크롤시 위에 고정
    window.addEventListener(`scroll`, handleScroll);
    return () => {
      window.removeEventListener(`scroll`, handleScroll);
    };
  }, [handleScroll]);
  useEffect(() => {
    // 로그인 되어있다면
    if (userStore.user) {
      userStore.loadUserInfo();
      setIsUsePaymentMadateList(true);
    }
  }, [userStore.loaded]);

  // 사용자정보, 모듈정보 로딩후 페이지 시작
  if (!userStore.loaded || !moduleLoaded) {
    return null;
  }
  return (
    <LayoutWithTitle location={location} title="후원하기" hideGnb>
      {/* 주의 : 최신 브라우저에서는 커스텀 메세지 출력 안됨 */}
      {/* https://stackoverflow.com/a/38880926 */}
      <BeforeUnloadComponent
        blockRoute={!isDataValid}
        modalComponentHandler={({ handleModalLeave, handleModalCancel }) => (
          <PopupModal
            onRequestClose={handleModalCancel}
            message="조금만 더 입력하시면 후원자님의 소중한 나눔이 어린이에게 전달될 수 있습니다."
            footer={
              <>
                <Button size="sm" outline onClick={handleModalLeave}>
                  취소
                </Button>
                <Button size="sm" onClick={handleModalCancel}>
                  계속
                </Button>
              </>
            }
          />
        )}
      >
        <SectionDonate ref={initFocus}>
          <Container ref={containerRef} sticky={isSticky}>
            <SupportStep sticky={isSticky}>
              <StepList active={currentStep === 1}>1.후원방법</StepList>
              <StepList active={currentStep === 2}>2.후원자 정보</StepList>
              <StepList active={currentStep === 3}>3.결제 정보</StepList>
            </SupportStep>

            <ToggleBox
              id="toggleStep1"
              active={currentStep === 1}
              hidden={currentStep < 1}
            >
              <StepHead>
                <h2>
                  <Tit size="s3">1. 후원 방법</Tit>
                </h2>
                {moduleBannerStep1 ? (
                  <DonateBanner
                    bannerinfo={moduleBannerStep1}
                    summaryText={step1SummaryText}
                    solicitCode={moduleData?.solicitCode}
                    key="donate-step-1"
                  />
                ) : step1SummaryText ? (
                  <>
                    <FormResult>{step1SummaryText}</FormResult>
                    {!moduleData && step1Data && currentStep !== 1 && (
                      <FormModify
                        type="button"
                        onClick={() => setCurrentStep(1)}
                      >
                        변경{` `}
                      </FormModify>
                    )}
                  </>
                ) : null}
              </StepHead>
              <StepBody>
                <DonateStep1
                  initialValues={
                    moduleData ||
                    sdoData || {
                      ...queryParams,
                      // 소문자로 통일하기로 함ㅠㅠ
                      solicitCode: queryParams.solicitcode,
                    }
                  }
                  onSubmit={onStep1Submit}
                  isSupportModule={!!moduleData}
                />
              </StepBody>
            </ToggleBox>
            <ToggleBox
              id="toggleStep2"
              active={currentStep === 2}
              hidden={currentStep < 2}
            >
              <StepHead showBlueBorder={currentStep === 2 && step2SummaryText}>
                <h2>
                  <Tit size="s3">2. 후원자 정보</Tit>
                </h2>

                {moduleBannerStep2 ? (
                  <DonateBanner
                    bannerinfo={moduleBannerStep2}
                    summaryText={step2SummaryText}
                    solicitCode={moduleData?.solicitCode}
                    key="donate-step-2"
                  />
                ) : step2SummaryText ? (
                  <>
                    <FormResult>{step2SummaryText}</FormResult>
                    {step2Data && currentStep !== 2 && (
                      <FormModify
                        type="button"
                        onClick={() => setCurrentStep(2)}
                      >
                        변경
                      </FormModify>
                    )}
                  </>
                ) : null}
              </StepHead>
              <StepBody>
                <DonateStep2
                  queryParams={queryParams}
                  step1Data={step1Data}
                  onSubmit={onStep2Submit}
                  selectedCategory={selectedCategory}
                  onLoadRegularPayInfo={(payInfo) => setRegularPayInfo(payInfo)}
                />
              </StepBody>
            </ToggleBox>
            <ToggleBox
              id="toggleStep3"
              active={currentStep === 3}
              hidden={currentStep < 3}
            >
              <StepHead>
                <h2>
                  <Tit size="s3">3. 결제 정보</Tit>
                </h2>
                <DonateBanner
                  bannerinfo={moduleBannerStep3}
                  solicitCode={moduleData?.solicitCode}
                  key="donate-step-3"
                />
              </StepHead>
              {/* 폼이 미리 생성되어있고, display: none일때 폼 데이터 세팅이 안되는 문제가 있어서 조건부 렌더링으로 수정 */}
              {currentStep === 3 && (
                <StepBody>
                  <DonateStep3
                    queryParams={queryParams}
                    onSubmit={onSubmit}
                    totalData={totalData}
                    currentRegularPayInfo={regularPayInfo}
                    moduleData={moduleData}
                    pgType={confPgType}
                    isUsePaymentMadateList={isUsePaymentMadateList}
                    savedValues={savedStep3Data}
                    onSaveValues={setSavedStep3Data}
                  />
                </StepBody>
              )}
            </ToggleBox>
          </Container>
        </SectionDonate>
      </BeforeUnloadComponent>
      {confPgType === 'KCP' ? (
        <>
          <KcpPcForm ref={kcpPcRef} />
          <KcpMobileForm ref={kcpMobileRef} />
        </>
      ) : (
        <>
          <InicisPcOnceForm ref={inicisPcRef} />
          <InicisMobileOnceForm ref={inicisMobileRef} />
        </>
      )}
      <NaverPayForm ref={naverRef} />
      <MobiliansOnceForm ref={mobiliansRef} />
      <DonateProcessOverlay stage={processStage} />
    </LayoutWithTitle>
  );
});

export default DonatePage;
